import {BasicDataTable, BasicDataTableConfig, DataTablesDataSource, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {promiseNow} from "../utils/promise";
import {debounce} from "@/shared/utils/misc";
import {AssignedUser, Identifiable} from "@/shared/timr";

export interface AssignmentPanelConfiguration<T, K = T> {
	panelElement: JQuery;
	assignedSourceProvider: () => PromiseLike<T[]>;
	selectSourceProvider: () => PromiseLike<K[]>;
	editMessage: string;
	saveMessage?: string;
	onOpen?: () => void;
}

export abstract class AssignmentPanel<T extends Identifiable, K = T> {
	protected static EVENT_ASSIGNED = "entryAssigned";
	protected static EVENT_UNASSIGNED = "entryRemoved";
	protected static EVENT_EDITMODE_CHANGED = "editModeChanged";

	private editMode: boolean;
	private assigned: T[];

	private overviewRow: JQuery;
	private assignmentRow: JQuery;
	private changeEditModeButton: JQuery;
	private filterInput: JQuery;

	constructor(protected config: AssignmentPanelConfiguration<T, K>,
				protected equalityFunction: (t1: T, t2: T) => boolean = (t1, t2) => t1.id === t2.id) {
		this.overviewRow = this.config.panelElement.find("div.overview-row");
		this.assignmentRow = this.config.panelElement.find("div.assignment-row");
		this.changeEditModeButton = this.config.panelElement.find("button.change-edit-mode");
		this.filterInput = this.config.panelElement.find("input.filter-input");

		config.editMessage = messages[config.editMessage] || config.editMessage;
		config.saveMessage = config.saveMessage || "admin.assignment.edit.end";
		config.saveMessage = messages[config.saveMessage] || config.saveMessage;

		this.changeEditModeButton.text(config.editMessage);
		this.editMode = false;

		this.assigned = [];

		this.changeEditModeButton.click(() => {
			if (this.config.onOpen) {
				this.config.onOpen();
			}
			this.setEditMode(!this.editMode);
		});

		this.filterInput.on("input", debounce(() => {
			const val = this.filterInput.val() as string;
			this.filterSelection(val);
		}, this, 350));
	}

	public reloadAssigned() {
		this.config.assignedSourceProvider().then((data) => {
			this.assigned = data;
			this.updateView();
		});
	}

	public assign(t: T) {
		if (!this.isAssigned(t)) {
			this.assigned.push(t);
			this.updateView();
			$(this).triggerHandler(AssignmentPanel.EVENT_ASSIGNED, t);
		}
	}

	public isAssigned(t: T) {
		return this.assigned.some(e => this.equalityFunction(e, t));
	}

	public unassign(t: T) {
		const filtered = this.assigned.filter(e => !this.equalityFunction(e, t));
		if (filtered.length !== this.assigned.length) {
			this.assigned = filtered;
			this.updateView();
			$(this).triggerHandler(AssignmentPanel.EVENT_UNASSIGNED, t);
		}
	}


	public getAssigned(): T[] {
		return this.assigned;
	}

	public updateView() {
		if (this.editMode) {
			this.updateEditView();
		} else {
			this.updateOverview();
		}
	}

	public setEditMode(editMode: boolean) {
		this.editMode = editMode;

		this.changeEditModeButton.text(editMode
			? this.config.saveMessage
			: this.config.editMessage)
			.toggleClass("btn-default", !editMode)
			.toggleClass("btn-primary", editMode);

		this.overviewRow.toggle(!editMode);
		this.assignmentRow.toggle(editMode);

		if (!editMode) {
			// Reset filter on finish assignment
			this.filterInput.val("");
			this.filterSelection("");
		}

		this.updateView();

		$(this).triggerHandler(AssignmentPanel.EVENT_EDITMODE_CHANGED);
	}

	public onAssignment(handler: (entry: T) => void) {
		$(this).on(AssignmentPanel.EVENT_ASSIGNED, (event, entry: T) => handler(entry));
	}

	public onUnassignment(handler: (entry: T) => void) {
		$(this).on(AssignmentPanel.EVENT_UNASSIGNED, (event, entry: T) => handler(entry));
	}

	public onEditModeChanged(handler: () => void) {
		$(this).on(AssignmentPanel.EVENT_EDITMODE_CHANGED, () => handler());
	}

	public isInEditMode(): boolean {
		return this.editMode;
	}

	public abstract setScrollHeight(height: number): void;

	public abstract filterSelection(filter: string): void;

	protected abstract updateEditView(): void;

	protected abstract updateOverview(): void;
}

export class PlaceholderAssignmentPanel<T extends Identifiable> extends AssignmentPanel<T> {
	constructor() {
		super({
			panelElement: $(),
			assignedSourceProvider: () => promiseNow([] as T[]),
			selectSourceProvider: () => promiseNow([] as T[]),
			editMessage: "",
		});
	}

	public filterSelection(filter: string) {
		// Empty implementation
	}

	public setScrollHeight(height: number) {
		// Empty implementation
	}

	protected updateEditView() {
		// Empty implementation
	}

	protected updateOverview() {
		// Empty implementation
	}
}

export interface AssignmentPanelComponent {
	setScrollHeight(height: number): void;

	searchOfflineData(filter: string): void;

	redrawCurrentPage(): void;

	reload(): void;
}

export abstract class BaseAssignmentTable<T extends Identifiable> extends BasicDataTable<T> {
	public static EVENT_ENTRY_REMOVED = "entryRemoved";

	constructor(dataSource: DataTablesDataSource<T>, public config: BasicDataTableConfig) {
		super(dataSource, config);

		this.config.element.on("click", "button.changeAssignmentButton", (e) => {
			const tr = $(e.target as HTMLElement).closest("tr");
			const entry = this.getRowData(tr);
			$(this).triggerHandler(BaseAssignmentTable.EVENT_ENTRY_REMOVED, entry);
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true
		};
	}

	protected renderControlButton(buttonClass: string, buttonTitle: string, buttonIcon: string): string {
		return `<button class="btn btn-default ${buttonClass}" title="${buttonTitle}" type="button"><a><i class="far ${buttonIcon}"></i></a></button>`;
	}
}

export class UserSelectTable extends BaseAssignmentTable<AssignedUser> {
	protected getColumnSettings(): TypedColumnSettings<AssignedUser>[] {
		return [
			{
				orderable: false,
				className: "no-padding-top no-padding-bottom no-padding-right",
				render: () => this.renderControlButton("changeAssignmentButton", messages["group.user.add"], "fa-plus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			},
			{data: "fullname", orderable: true, render: BasicDataTable.renderText, searchable: true}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "asc"]];
	}
}
